// Materials.js - Real Texture-Based Cerakote System
import * as THREE from 'three'
import { useMemo, useRef, useState, useCallback, useEffect } from 'react'
import { useTexture } from '@react-three/drei'

/*
==================== BANDING SOLUTION ====================

PROBLEM: Horizontal banding appeared on curved surfaces
CAUSE: Shadow rendering (castShadow/receiveShadow) creating discrete steps on curved geometry
SOLUTION: Disabled all shadows (Canvas shadows={false} + removed castShadow from lights)

This eliminated the banding completely while maintaining good visual quality.
The textures, materials, and lighting all work correctly - it was purely shadow-based.
*/

// ==================== TEXTURE URLS ====================

// ==================== SWATCH TEXTURE URLS ====================
// One swatch per color — used directly as the material map

// Universal cerakote surface detail maps — shared across all colors
const CERAKOTE_NORMAL_MAP = 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/cerakote_normal_map.webp'
const CERAKOTE_ROUGHNESS_MAP = 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/cerakote_roughness_map.webp'

const SWATCHES = {
  '#ebe9e1': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_White_seamless.webp',
  '#d7be88': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Coffee-Cream_seamless.webp',
  '#645648': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Smoked-Bronze_seamless.webp',
  '#1c1d1d': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Graphite-Black_seamless.webp',
  '#975b3a': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Terracotta_seamless.webp',
  '#a16f48': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Copper_seamless.webp',
  '#d8763c': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Burnt-Orange_seamless.webp',
  '#ce984c': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Gold_seamless.webp',
  '#d4a53a': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Sunflower_seamless.webp',
  '#ffca0d': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Formula-Yellow_seamless.webp',
  '#a4402c': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Calabrian-Red_seamless.webp',
  '#5e2833': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Spiced-Berry_seamless.webp',
  '#802641': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Dark-Fuchsia_seamless.webp',
  '#56475e': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Vera-Violet_seamless.webp',
  '#926464': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Rose-Gold_seamless.webp',
  '#c68883': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Petal-Pink_seamless.webp',
  '#79712f': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Mosslight-Green_seamless.webp',
  '#5e6d35': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260406_Swatches_Verdant-Green_seamless.webp',
  '#394a37': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Evergreen_seamless.webp',
  '#344e4a': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Meridian-Blue_seamless.webp',
  '#196962': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Turquoise-Teal_seamless.webp',
  '#4db494': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Aqua-Lucia_seamless.webp',
  // Satin Aluminum uses its own swatch
  '#9c9e9e': 'https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/260317_Swatches_Satin-Alluminum_seamless.webp'
}

// Tint multiplier per color — darkens texture to match actual finish
// Three.js multiplies map * color, so use grey values to darken
const SWATCH_TINTS = {
  '#ebe9e1': '#ffffff',
  '#d7be88': '#cccccc',
  '#645648': '#888880',
  '#1c1d1d': '#aaaaaa',
  '#975b3a': '#cccccc',
  '#a16f48': '#bbbbbb',
  '#d8763c': '#cccccc',
  '#ce984c': '#cccccc',
  '#d4a53a': '#cccccc',
  '#ffca0d': '#cccccc',
  '#a4402c': '#cccccc',
  '#5e2833': '#cccccc',
  '#802641': '#bbbbbb',
  '#56475e': '#bbbbbb',
  '#926464': '#cccccc',
  '#c68883': '#dddddd',
  '#79712f': '#bbbbbb',
  '#5e6d35': '#aaaaaa',
  '#394a37': '#aaaaaa',
  '#344e4a': '#aaaaaa',
  '#196962': '#bbbbbb',
  '#4db494': '#cccccc',
  '#9c9e9e': '#dddddd'
}

// Per-color multipliers (linear RGB) so each paint color under the virtual studio lighting
// matches its product photo on ohmaworld.com. Measured on the front of the bottom screen.
// Colors not listed here fall back to SWATCH_TINTS above.
const SWATCH_CALIBRATION = {
  '#4db494': [0.832, 0.511, 0.658], // Aqua Lucia
  '#d8763c': [0.627, 0.579, 0.346], // Burnt Orange
  '#a4402c': [1.113, 0.924, 0.949], // Calabrian Red
  '#a16f48': [1.073, 0.833, 0.609], // Copper
  '#802641': [1.045, 0.354, 0.817], // Dark Fuchsia
  '#394a37': [1.311, 1.07, 1.294], // Evergreen
  '#ce984c': [0.528, 0.501, 0.419], // Gold
  '#344e4a': [1.249, 1.359, 1.922], // Meridian Blue
  '#79712f': [0.942, 0.811, 0.425], // Mosslight Green
  '#c68883': [0.631, 0.593, 0.63], // Petal Pink
  '#926464': [1.132, 1.243, 1.136], // Rose Gold
  '#645648': [0.394, 0.358, 0.336], // Smoked Bronze
  '#5e2833': [0.749, 0.535, 0.418], // Spiced Berry
  '#d4a53a': [0.625, 0.537, 0.001], // Sunflower
  '#975b3a': [0.581, 0.297, 0.069], // Terracotta
  '#196962': [0.142, 0.786, 1.101], // Turquoise Teal
  '#56475e': [0.879, 0.841, 1.147], // Vera Violet
  '#5e6d35': [0.599, 0.635, 0.41], // Verdant Green
  '#9c9e9e': [0.86, 0.88, 0.87], // Satin Aluminum (stem brightness from the photo)
}

// Per-color finish. Every paint is cerakote shot through 100 grit at 14:1, but they split in two:
// the flat paints lay down smooth and matte, while the rough group dries with a fine speckled texture.
// At normal viewing distance that speckle is smaller than a pixel, so it reads as a broader, duller
// highlight (higher roughness); up close the speckle itself comes from the swatch photo, which has
// 2-10x the fine texture of the flat paints. Copper and Rose Gold keep a little metallic highlight
// (measured from the stem in the product photos). Satin Aluminum is bare metal, sandblasted with
// 100 grit aluminum oxide under a thin clear coat.
const FINISH = {
  flat: { roughness: 0.8, metalness: 0.0, envMapIntensity: 1.0 },
  rough: { roughness: 0.92, metalness: 0.1, envMapIntensity: 1.0 },
  roughMetallic: { roughness: 0.82, metalness: 0.3, envMapIntensity: 1.05 }, // flake map available (flake: 0.x) but the body UVs stretch it into streaks, so it stays off
  blasted: { roughness: 0.86, metalness: 0.55, envMapIntensity: 1.0 }
}
const SWATCH_FINISH = {
  // rough, speckled
  '#975b3a': 'rough', // Terracotta
  '#645648': 'rough', // Smoked Bronze
  '#d8763c': 'rough', // Burnt Orange
  '#ce984c': 'rough', // Gold
  '#d4a53a': 'rough', // Sunflower
  '#a4402c': 'rough', // Calabrian Red
  '#5e2833': 'rough', // Spiced Berry
  '#a16f48': 'roughMetallic', // Copper
  '#926464': 'roughMetallic', // Rose Gold
  '#9c9e9e': 'blasted', // Satin Aluminum
  // flat, matte
  '#344e4a': 'flat', // Meridian Blue
  '#79712f': 'flat', // Mosslight Green
  '#4db494': 'flat', // Aqua Lucia
  '#c68883': 'flat', // Petal Pink
  '#56475e': 'flat', // Vera Violet
  '#196962': 'flat', // Turquoise Teal
  '#394a37': 'flat', // Evergreen
  '#5e6d35': 'flat', // Verdant Green
  '#802641': 'flat' // Dark Fuchsia
}

// METALS - polished metal is mostly a mirror of its surroundings. The photos were shot on a white sweep,
// so the brass and stainless mirror a bright room; the paint studio environment is dark between the light
// panels (which is right for the cerakote), so metals get their own environment with a brighter room
// (rendered by StudioEnvironment into METAL_ENV.target.environment).
// three.js r156 bug: the fragment shader of an anisotropic material reads the vUv varying, but the vertex
// shader is never given USE_ANISOTROPY, so it doesn't write it and the program fails to link ("varying vUv
// does not match"). Always writing vUv in the vertex shader fixes it for every material; it's one varying.
THREE.ShaderChunk.uv_pars_vertex = THREE.ShaderChunk.uv_pars_vertex.replace('#if defined( USE_UV ) || defined( USE_ANISOTROPY )', '#if 1')
THREE.ShaderChunk.uv_vertex = THREE.ShaderChunk.uv_vertex.replace('#if defined( USE_UV ) || defined( USE_ANISOTROPY )', '#if 1')

export const METAL_ENV = { target: new THREE.Scene() }
export const METAL = {
  // testing overrides: ?lightdebug&metal=key:value,key:value
  bodyRoughness: 0.21, // brass body, polished (unchanged from the original builder)
  screenRoughness: 0.3, // brushed brass screens
  polishedScreenRoughness: 0.21, // brass screens on a brass body: same as the body
  stainlessRoughness: 0.32,
  metalness: 0.88,
  envMapIntensity: 0.95,
  screenEnvMapIntensity: 0.95,
  // Brass screens use the original stainless brushed texture, tinted. Light champagne brass, like the
  // lighter-screen photos (the hand-brushed screens range lighter to darker with the light)
  brassR: 0.88, brassG: 0.74, brassB: 0.5, // hue measured on the light-screen black/brass photo
  // brass screens on a brass body: the body's own brass colour (brass_smooth.webp, sampled raw: 212,181,85)
  polishedBrassR: 0.83, polishedBrassG: 0.675, polishedBrassB: 0.356, // body brass x bodyTint
  // brass body: the original texture reads green-yellow next to the photos; this warms it (linear RGB)
  bodyTintR: 1, bodyTintG: 0.95, bodyTintB: 1.08,
  // brushed highlight: reflections stretch across the brushing, like real brushed metal (0 = off)
  brassAniso: 0.4, stainlessAniso: 0.4, polishedAniso: 0.25, anisoRotation: 1.5708,
  // hand-polished cloudiness (see setDetail): brightness and roughness swing of the soft patches
  brassCloud: 0.2, brassCloudRough: 0.3, brassContrast: 1.15, brassStrokes: 0.06,
  stainlessCloud: 0.09, stainlessCloudRough: 0.2, stainlessStrokes: 0.045,
  // hand-brushing irregularity: faint strokes a few degrees off the main brushing, varied length
  polishedContrast: 0.6, polishedCloud: 0.1, polishedStrokes: 0.04
}
try {
  const q = new URLSearchParams(window.location.search)
  if (q.has('lightdebug') && q.get('metal')) {
    q.get('metal').split(',').forEach((kv) => {
      const [k, v] = kv.split(':')
      if (k in METAL) METAL[k] = isNaN(parseFloat(v)) ? v : parseFloat(v)
    })
  }
} catch (e) {}

// SURFACE DETAIL - small shader additions, driven by one tiny tileable texture (public/tex/v6/detail.png,
// made by bake/metaltex/detail.py):
//  mode 1 (brass / stainless screens): soft hand-polished cloudiness on top of the original brushed
//    texture: slightly brighter, smoother patches and slightly duller ones, plus faint strokes that wander
//    a few degrees off the brushing (hand brushing is never perfectly parallel); the brushed texture's own
//    contrast can be turned down (polished brass on a brass body)
//  mode 2 (paint): the swatch is sampled twice at incommensurate scales and blended with a smooth mask,
//    so the tile repeat stops showing
// Uniforms live in a WeakMap (not userData, which three.js deep-copies on clone).
const METAL_TEX_ORIGIN = (() => {
  try {
    const src = document.currentScript && document.currentScript.src
    return src ? new URL(src).origin : window.location.origin
  } catch (e) {
    return ''
  }
})()
let _detailTex = null
function detailTexture() {
  if (_detailTex) return _detailTex
  const t = new THREE.TextureLoader().load(`${METAL_TEX_ORIGIN}/tex/v6/detail.png`)
  t.wrapS = t.wrapT = THREE.RepeatWrapping
  _detailTex = t
  return t
}
const DETAIL = new WeakMap()
const DETAIL_VERT_PARS = 'uniform vec2 uDetailRepeat;\nvarying vec2 vDetailUv;\n'
const DETAIL_FRAG_PARS = 'uniform sampler2D uDetailMap;\nuniform int uDetailMode;\nuniform float uCloudAmt;\nuniform float uCloudRough;\nuniform float uMapContrast;\nuniform float uMapMean;\nuniform float uTileScale;\nuniform float uScratchAmt;\nvarying vec2 vDetailUv;\n'
const DETAIL_MAP = `
#ifdef USE_MAP
  vec4 sampledDiffuseColor = texture2D( map, vMapUv );
  if ( uDetailMode == 2 ) {
    vec4 sampleB = texture2D( map, vMapUv * uTileScale + vec2( 0.37, 0.61 ) );
    float w = smoothstep( 0.36, 0.64, texture2D( uDetailMap, vDetailUv ).g );
    sampledDiffuseColor = mix( sampledDiffuseColor, sampleB, w );
  }
  if ( uDetailMode == 1 ) sampledDiffuseColor.rgb = mix( vec3( uMapMean ), sampledDiffuseColor.rgb, uMapContrast );
  diffuseColor *= sampledDiffuseColor;
#endif
float detailCloud = 0.0;
if ( uDetailMode == 1 ) {
  detailCloud = texture2D( uDetailMap, vDetailUv ).r - 0.5;
  float detailStroke = texture2D( uDetailMap, vDetailUv * 4.0 ).b - 0.5;
  diffuseColor.rgb *= ( 1.0 + uCloudAmt * detailCloud * 2.0 ) * ( 1.0 + uScratchAmt * detailStroke * 2.0 );
}
`
function detailUniforms(material) {
  let u = DETAIL.get(material)
  if (u) return u
  u = {
    uDetailMap: { value: detailTexture() },
    uDetailRepeat: { value: new THREE.Vector2(1, 1) },
    uDetailMode: { value: 0 },
    uCloudAmt: { value: 0 },
    uCloudRough: { value: 0 },
    uMapContrast: { value: 1 },
    uMapMean: { value: 0.68 },
    uTileScale: { value: 0.731 },
    uScratchAmt: { value: 0 }
  }
  DETAIL.set(material, u)
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, u)
    shader.vertexShader = shader.vertexShader
      .replace('#include <uv_pars_vertex>', '#include <uv_pars_vertex>\n' + DETAIL_VERT_PARS)
      .replace('#include <uv_vertex>', '#include <uv_vertex>\nvDetailUv = uv * uDetailRepeat;')
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <uv_pars_fragment>', '#include <uv_pars_fragment>\n' + DETAIL_FRAG_PARS)
      .replace('#include <map_fragment>', DETAIL_MAP)
      .replace(
        '#include <roughnessmap_fragment>',
        '#include <roughnessmap_fragment>\nif ( uDetailMode == 1 ) roughnessFactor *= 1.0 - uCloudRough * detailCloud * 2.0;'
      )
  }
  material.customProgramCacheKey = () => 'ohmaDetail2'
  material.needsUpdate = true
  return u
}
// mode 0 = off (no-op if the material never had detail)
function setDetail(material, mode, opts = {}) {
  if (!mode && !DETAIL.has(material)) return
  const u = detailUniforms(material)
  u.uDetailMode.value = mode
  if (opts.repeat) u.uDetailRepeat.value.set(opts.repeat[0], opts.repeat[1])
  u.uCloudAmt.value = opts.cloud ?? 0
  u.uCloudRough.value = opts.cloudRough ?? 0
  u.uMapContrast.value = opts.contrast ?? 1
  u.uScratchAmt.value = opts.strokes ?? 0
  if (opts.mapMean !== undefined) u.uMapMean.value = opts.mapMean
}
// Cloud patch size on the screens: one detail tile = 140 model units (~6 cm). Screen UVs advance
// 0.0122 per unit up the screen and 0.048 per unit across.
const METAL_CLOUD_REPEAT = [1 / (140 * 0.0122), 1 / (140 * 0.048)]
// The original builder stainless texture averages 174/255 and is sampled as raw values (0.68)
const BRUSHED_MEAN = 0.68
const metalTint = (material, lin) => material.color.setRGB(lin[0] / BRUSHED_MEAN, lin[1] / BRUSHED_MEAN, lin[2] / BRUSHED_MEAN, THREE.LinearSRGBColorSpace)
// paint: the anti-tiling mask spans ~4.3 swatch tiles
const PAINT_DETAIL = { repeat: [0.23, 0.23] }

function setMetalEnv(material, on) {
  material.userData.metalEnv = !!on
  material.envMap = on ? METAL_ENV.target.environment || null : null
}

// METAL FLAKE - Copper and Rose Gold carry real metal flake that throws tiny glints. A procedural normal
// map of randomly tilted cells (about a quarter of a millimeter each, the same aspect as the swatch
// textures) breaks the highlight into sparkle up close; from further away it averages out to the
// plain finish, which is also how the real paint reads.
let _flakeMap = null
function flakeNormalMap() {
  if (_flakeMap) return _flakeMap
  const W = 1024, H = 256, CELL = 6
  const data = new Uint8Array(W * H * 4)
  let seed = 1337
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647)
  const cw = W / CELL, ch = H / CELL
  const cells = new Float32Array(cw * ch * 2)
  for (let i = 0; i < cw * ch; i++) {
    // most flakes lie nearly flat, a few tilt far enough to catch a light
    const r = Math.pow(rnd(), 2.2) * 0.9
    const a = rnd() * Math.PI * 2
    cells[i * 2] = Math.cos(a) * r
    cells[i * 2 + 1] = Math.sin(a) * r
  }
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const c = (Math.floor(y / CELL) * cw + Math.floor(x / CELL)) * 2
      const nx = cells[c], ny = cells[c + 1]
      const nz = Math.sqrt(Math.max(0, 1 - nx * nx - ny * ny))
      const o = (y * W + x) * 4
      data[o] = Math.round((nx * 0.5 + 0.5) * 255)
      data[o + 1] = Math.round((ny * 0.5 + 0.5) * 255)
      data[o + 2] = Math.round((nz * 0.5 + 0.5) * 255)
      data[o + 3] = 255
    }
  }
  const tex = new THREE.DataTexture(data, W, H, THREE.RGBAFormat)
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping
  tex.magFilter = THREE.LinearFilter
  tex.minFilter = THREE.LinearMipmapLinearFilter
  tex.generateMipmaps = true
  tex.anisotropy = 8
  tex.needsUpdate = true
  _flakeMap = tex
  return tex
}

let SWATCH_SCALE = 1 // 2 and 4 (closer to the photo grit on paper) read too grainy on screen
try {
  const q = new URLSearchParams(window.location.search)
  if (q.has('lightdebug') && q.get('swscale')) SWATCH_SCALE = parseFloat(q.get('swscale')) || SWATCH_SCALE
} catch (e) {}

// Testing only: with ?lightdebug, &fin=645648:0.8:0.2:1;975b3a:0.8:0.2:1 sets roughness:metalness:env per color
const FINISH_OVERRIDE = (() => {
  try {
    const q = new URLSearchParams(window.location.search)
    if (!q.has('lightdebug') || !q.get('fin')) return {}
    return Object.fromEntries(
      q
        .get('fin')
        .split(';')
        .map((e) => e.split(':'))
        .filter((a) => a.length === 4)
        .map(([h, r, m, e]) => ['#' + h.toLowerCase(), { roughness: +r, metalness: +m, envMapIntensity: +e }])
    )
  } catch (e) {
    return {}
  }
})()

// ==================== MATERIAL PRESETS ====================

// Standard Cerakote - using real textures
export const CERAKOTE_PROPS = {
  roughness: 0.75,
  metalness: 0.1,
  envMapIntensity: 1,
  normalScale: 0,
  clearcoat: 0.1,
  clearcoatRoughness: 0.4
}

// ==================== MATERIAL HOOK ====================

export function useCerakoteMaterials(texDebugValues = null, matDebugRef = null, matDebug = null) {
  // Internal ref so apply functions always access latest matDebug values
  const _mat = useRef(null)
  _mat.current = matDebug ?? matDebugRef?.current ?? null
  // Paint swatches: only white (the base for custom colors and the stand-in while a swatch loads), black
  // (brass-body accessories) and the default color hold up the first view. The other 20 (about 1 MB)
  // follow in the background as soon as the mic is on screen.
  const EAGER_SWATCHES = ['#ebe9e1', '#1c1d1d', '#d7be88']
  const eagerSwatches = useTexture(EAGER_SWATCHES.map((h) => SWATCHES[h]))
  const swatchRaw = useRef({})
  EAGER_SWATCHES.forEach((h, i) => {
    if (!swatchRaw.current[h]) swatchRaw.current[h] = eagerSwatches[i]
  })
  const swatchLoading = useRef({})
  const [swatchVersion, setSwatchVersion] = useState(0)
  const requestSwatch = useCallback((hex) => {
    if (!SWATCHES[hex] || swatchRaw.current[hex] || swatchLoading.current[hex]) return
    swatchLoading.current[hex] = true
    new THREE.TextureLoader().load(
      SWATCHES[hex],
      (t) => {
        swatchRaw.current[hex] = t
        setSwatchVersion((v) => v + 1) // re-renders the mic, which re-runs the material pass
      },
      undefined,
      () => {
        swatchLoading.current[hex] = false
      }
    )
  }, [])
  // Right after the mic first shows, fetch the rest in the background (one re-render when they're all in),
  // so picking a color is instant again by the time anyone opens the color list.
  useEffect(() => {
    const t = setTimeout(() => {
      const loader = new THREE.TextureLoader()
      const todo = Object.keys(SWATCHES).filter((h) => !swatchRaw.current[h] && !swatchLoading.current[h])
      todo.forEach((h) => (swatchLoading.current[h] = true))
      Promise.all(
        todo.map(
          (h) =>
            new Promise((res) =>
              loader.load(
                SWATCHES[h],
                (tex) => {
                  swatchRaw.current[h] = tex
                  res()
                },
                undefined,
                () => {
                  swatchLoading.current[h] = false
                  res()
                }
              )
            )
        )
      ).then(() => setSwatchVersion((v) => v + 1))
    }, 300)
    return () => clearTimeout(t)
  }, [])
  const swatchClones = useRef({ body: {}, screen: {} })
  const swatchMap = (kind) => {
    const map = {}
    Object.keys(swatchRaw.current).forEach((hex) => {
      let t = swatchClones.current[kind][hex]
      if (!t) {
        t = swatchRaw.current[hex].clone()
        t.encoding = THREE.sRGBEncoding
        t.wrapS = t.wrapT = THREE.RepeatWrapping
        // Grain size: SWATCH_SCALE enlarges the swatch photo grain (1 = as photographed)
        t.repeat.set(1 / SWATCH_SCALE, 1 / SWATCH_SCALE)
        t.anisotropy = 8 // keeps the paint speckle crisp on the side walls at grazing angles
        t.needsUpdate = true
        swatchClones.current[kind][hex] = t
      }
      map[hex] = t
    })
    return map
  }
  const bodyTextures = useMemo(() => swatchMap('body'), [swatchVersion, eagerSwatches])
  const screenTextures = useMemo(() => swatchMap('screen'), [swatchVersion, eagerSwatches])

  // Universal surface detail maps shared across all Cerakote colors
  const cerakoteNormal = useTexture(CERAKOTE_NORMAL_MAP)
  const cerakoteRoughness = useTexture(CERAKOTE_ROUGHNESS_MAP)

  const [bodyNormal, bodyRoughness, screenNormal, screenRoughness] = useMemo(() => {
    const bn = cerakoteNormal.clone()
    bn.wrapS = bn.wrapT = THREE.RepeatWrapping
    bn.repeat.set(1, 1)
    bn.needsUpdate = true

    const br = cerakoteRoughness.clone()
    br.wrapS = br.wrapT = THREE.RepeatWrapping
    br.repeat.set(1, 1)
    br.needsUpdate = true

    const sn = cerakoteNormal.clone()
    sn.wrapS = sn.wrapT = THREE.RepeatWrapping
    sn.repeat.set(1, 1)
    sn.needsUpdate = true

    const sr = cerakoteRoughness.clone()
    sr.wrapS = sr.wrapT = THREE.RepeatWrapping
    sr.repeat.set(1, 1)
    sr.needsUpdate = true

    return [bn, br, sn, sr]
  }, [cerakoteNormal, cerakoteRoughness])

  const applySwatchMaterial = (material, color, textureMap, templateTex, normalMap, roughnessMap, normalScale = 0) => {
    // Normalize black variants to canonical hex
    const hex = String(color)
      .toLowerCase()
      .replace('#1a1a1a', '#1c1d1d')
      .replace('#2f2e34', '#1c1d1d')
      .replace('#2a2a2a', '#1c1d1d')
      .replace('#000000', '#1c1d1d')
      .replace('#404040', '#1c1d1d')
    const tex = textureMap[hex]
    if (!tex) requestSwatch(hex) // until it arrives, the white swatch tinted to the color stands in
    const tint = SWATCH_TINTS[hex] || null

    if (tex) {
      // Known color — use its swatch with the preset tint.
      // Calibrated colors use a linear RGB multiplier matched to the product photos.
      material.map = tex
      const cal = SWATCH_CALIBRATION[hex]
      if (cal) material.color.setRGB(cal[0], cal[1], cal[2])
      else material.color.set(tint || '#cccccc')
    } else if (templateTex) {
      // Custom RGB — use white swatch texture, tinted with custom color boosted slightly
      const c = new THREE.Color(color)
      c.r = Math.min(1, c.r * 1.05)
      c.g = Math.min(1, c.g * 1.05)
      c.b = Math.min(1, c.b * 1.05)
      material.map = templateTex
      material.color.copy(c)
    } else {
      material.map = null
      material.color.set(color)
    }

    // Layer cerakote surface detail maps
    setMetalEnv(material, false)
    if (material.map) setDetail(material, 2, PAINT_DETAIL)
    else setDetail(material, 0)
    if (material.anisotropy !== undefined) material.anisotropy = 0
    material.normalMap = normalMap || null
    material.normalScale = new THREE.Vector2(normalScale, normalScale)
    material.roughnessMap = roughnessMap || null
    material.metalnessMap = null
    material.aoMap = null
    material.alphaMap = null
    material.transparent = false
    material.opacity = 1
    material.roughness = _mat.current?.cerakoteRoughness ?? CERAKOTE_PROPS.roughness
    material.metalness = _mat.current?.cerakoteMetalness ?? CERAKOTE_PROPS.metalness
    material.envMapIntensity = _mat.current?.cerakoteEnvMap ?? CERAKOTE_PROPS.envMapIntensity
    if (material.clearcoat !== undefined) material.clearcoat = _mat.current?.cerakoteClearcoat ?? CERAKOTE_PROPS.clearcoat
    if (material.clearcoatRoughness !== undefined) material.clearcoatRoughness = _mat.current?.cerakoteClearcoatRoughness ?? CERAKOTE_PROPS.clearcoatRoughness
    const finish = FINISH_OVERRIDE[hex] || FINISH[SWATCH_FINISH[hex]]
    if (finish) {
      material.roughness = finish.roughness
      material.metalness = finish.metalness
      material.envMapIntensity = finish.envMapIntensity
      if (finish.flake && !normalScale) {
        material.normalMap = flakeNormalMap()
        material.normalScale = new THREE.Vector2(finish.flake, finish.flake)
      }
    }
    material.needsUpdate = true
  }

  // Neutral template texture for custom RGB colors — white swatch tints most accurately to any hue
  const customColorTemplateBody = useMemo(() => {
    const raw = eagerSwatches[0] // White swatch as neutral base
    if (!raw) return null
    const t = raw.clone()
    t.encoding = THREE.sRGBEncoding
    t.wrapS = t.wrapT = THREE.RepeatWrapping
    t.repeat.set(1 / SWATCH_SCALE, 1 / SWATCH_SCALE)
    t.needsUpdate = true
    return t
  }, [eagerSwatches])

  const customColorTemplateScreen = useMemo(() => {
    const raw = eagerSwatches[0] // White swatch as neutral base
    if (!raw) return null
    const t = raw.clone()
    t.encoding = THREE.sRGBEncoding
    t.wrapS = t.wrapT = THREE.RepeatWrapping
    t.repeat.set(1 / SWATCH_SCALE, 1 / SWATCH_SCALE)
    t.needsUpdate = true
    return t
  }, [eagerSwatches])

  // Apply debug repeat/normal overrides when texDebugValues changes
  const dbg = texDebugValues
  const applyCerakoteMaterial = (material, color) => {
    const normalScale = dbg ? dbg.bodyNormal : 0
    applySwatchMaterial(material, color, bodyTextures, customColorTemplateBody, bodyNormal, bodyRoughness, normalScale)
    // Apply dynamic repeat if debug active
    if (dbg && material.map) material.map.repeat.set(dbg.bodyRepeat, dbg.bodyRepeat)
    if (dbg && material.normalMap) material.normalMap.repeat.set(dbg.bodyRepeat, dbg.bodyRepeat)
    if (dbg && material.roughnessMap) material.roughnessMap.repeat.set(dbg.bodyRepeat, dbg.bodyRepeat)
  }

  const applyScreenMaterial = (material, color) => {
    const normalScale = dbg ? dbg.screenNormal : 0
    applySwatchMaterial(material, color, screenTextures, customColorTemplateScreen, screenNormal, screenRoughness, normalScale)
    if (dbg && material.map) material.map.repeat.set(dbg.screenRepeat, dbg.screenRepeat)
    if (dbg && material.normalMap) material.normalMap.repeat.set(dbg.screenRepeat, dbg.screenRepeat)
    if (dbg && material.roughnessMap) material.roughnessMap.repeat.set(dbg.screenRepeat, dbg.screenRepeat)
  }

  // The original builder brass and stainless (textures and settings as they were), now reflecting the
  // metal studio so they don't go dark. Brass screens on a brass body use the body's smooth brass,
  // a touch rougher than the body, so they read polished instead of brushed.
  // Brass screens: the original builder's brushed stainless texture (same mapping as the stainless screens),
  // tinted brass, with soft hand-polished patches. On a brass body the brushing is toned down so the
  // screens read polished like the body.
  const applyBrassMaterial = (material, brassTextures, polished = false) => {
    material.map = brassTextures.brushed || brassTextures.baseColor
    metalTint(material, polished ? [METAL.polishedBrassR, METAL.polishedBrassG, METAL.polishedBrassB] : [METAL.brassR, METAL.brassG, METAL.brassB])
    material.normalMap = null
    material.normalScale = new THREE.Vector2(0, 0)
    material.roughnessMap = null
    material.metalnessMap = null
    material.aoMap = brassTextures.ao
    material.aoMapIntensity = 0.8
    material.emissive.set('#000000')
    material.emissiveIntensity = 0
    material.roughness = polished ? METAL.polishedScreenRoughness : METAL.screenRoughness
    material.metalness = _mat.current?.metalMetalness ?? METAL.metalness
    material.envMapIntensity = _mat.current?.metalEnvMap ?? METAL.screenEnvMapIntensity
    if (material.clearcoat !== undefined) material.clearcoat = _mat.current?.metalClearcoat ?? 0
    if (material.clearcoatRoughness !== undefined) material.clearcoatRoughness = _mat.current?.metalClearcoatRoughness ?? 0.1
    if (material.anisotropy !== undefined) {
      material.anisotropy = polished ? METAL.polishedAniso : METAL.brassAniso
      material.anisotropyRotation = METAL.anisoRotation
    }
    setMetalEnv(material, true)
    setDetail(material, 1, polished
      ? { repeat: METAL_CLOUD_REPEAT, cloud: METAL.polishedCloud, cloudRough: 0.15, contrast: METAL.polishedContrast, strokes: METAL.polishedStrokes, mapMean: BRUSHED_MEAN }
      : { repeat: METAL_CLOUD_REPEAT, cloud: METAL.brassCloud, cloudRough: METAL.brassCloudRough, contrast: METAL.brassContrast, strokes: METAL.brassStrokes, mapMean: BRUSHED_MEAN })
    material.needsUpdate = true
  }

  const applyBrassBodyMaterial = (material, brassTextures) => {
    material.map = brassTextures?.baseColor || null
    material.color.setRGB(METAL.bodyTintR, METAL.bodyTintG, METAL.bodyTintB, THREE.LinearSRGBColorSpace)
    const normalScale = _mat.current?.brassBodyNormalScale ?? 0
    const normalTiling = _mat.current?.brassBodyNormalTiling ?? 1
    const normalMap = brassTextures?.normal || null
    material.normalMap = normalScale > 0 ? normalMap : null
    material.normalScale = new THREE.Vector2(normalScale, normalScale)
    if (normalMap && normalScale > 0) {
      normalMap.repeat.set(normalTiling, normalTiling)
      normalMap.needsUpdate = true
    }
    material.roughnessMap = null
    material.roughness = _mat.current?.metalRoughness ?? METAL.bodyRoughness
    material.metalnessMap = null
    material.aoMap = brassTextures?.ao || null
    material.aoMapIntensity = 0.8
    material.emissive.set('#000000')
    material.emissiveIntensity = 0
    material.metalness = _mat.current?.metalMetalness ?? METAL.metalness
    material.envMapIntensity = _mat.current?.metalEnvMap ?? METAL.envMapIntensity
    if (material.clearcoat !== undefined) material.clearcoat = _mat.current?.metalClearcoat ?? 0
    if (material.clearcoatRoughness !== undefined) material.clearcoatRoughness = _mat.current?.metalClearcoatRoughness ?? 0.1
    if (material.anisotropy !== undefined) material.anisotropy = 0
    setMetalEnv(material, true)
    setDetail(material, 0)
    material.needsUpdate = true
  }

  // Stainless screens: the original builder texture and mapping, plus a little hand-polished unevenness
  const applyStainlessMaterial = (material, stainlessTexture) => {
    material.map = stainlessTexture
    material.color.set('#ffffff')
    material.normalMap = null
    material.roughnessMap = null
    material.metalnessMap = null
    material.aoMap = null
    material.roughness = _mat.current?.metalRoughness ?? METAL.stainlessRoughness
    material.metalness = _mat.current?.metalMetalness ?? METAL.metalness
    material.envMapIntensity = _mat.current?.metalEnvMap ?? METAL.screenEnvMapIntensity
    if (material.clearcoat !== undefined) material.clearcoat = _mat.current?.metalClearcoat ?? 0
    if (material.clearcoatRoughness !== undefined) material.clearcoatRoughness = _mat.current?.metalClearcoatRoughness ?? 0.1
    if (material.anisotropy !== undefined) {
      material.anisotropy = METAL.stainlessAniso
      material.anisotropyRotation = METAL.anisoRotation
    }
    material.emissive.set('#000000')
    material.emissiveIntensity = 0
    setMetalEnv(material, true)
    setDetail(material, 1, { repeat: METAL_CLOUD_REPEAT, cloud: METAL.stainlessCloud, cloudRough: METAL.stainlessCloudRough, contrast: 1, strokes: METAL.stainlessStrokes, mapMean: BRUSHED_MEAN })
    material.needsUpdate = true
  }

  const applyAccessoryMaterial = (material, color, isBrassBody = false) => {
    if (isBrassBody) {
      const blackTex = bodyTextures['#1c1d1d']
      if (blackTex) {
        material.map = blackTex
        material.color.set(SWATCH_TINTS['#1c1d1d'] || '#aaaaaa')
      } else {
        material.map = null
        material.color.set('#1c1d1d')
      }
      material.normalMap = null
      material.normalScale = new THREE.Vector2(1.0, 1.0)
      material.roughnessMap = null
      material.metalnessMap = null
      material.aoMap = null
      material.roughness = CERAKOTE_PROPS.roughness
      material.metalness = CERAKOTE_PROPS.metalness
      material.envMapIntensity = CERAKOTE_PROPS.envMapIntensity
      material.needsUpdate = true
    } else {
      const normalScale = dbg ? dbg.popFilterNormal : 0
      applySwatchMaterial(material, color, bodyTextures, customColorTemplateBody, bodyNormal, bodyRoughness, normalScale)
      if (dbg && material.map) material.map.repeat.set(dbg.popFilterRepeat, dbg.popFilterRepeat)
      if (dbg && material.normalMap) material.normalMap.repeat.set(dbg.popFilterRepeat, dbg.popFilterRepeat)
      if (dbg && material.roughnessMap) material.roughnessMap.repeat.set(dbg.popFilterRepeat, dbg.popFilterRepeat)
    }
  }

  return {
    applyCerakoteMaterial,
    applyScreenMaterial,
    applyBrassMaterial,
    applyBrassBodyMaterial,
    applyStainlessMaterial,
    applyAccessoryMaterial
  }
}

// ==================== LIGHTING CONFIGURATIONS ====================

// Balanced lighting - bright, even studio-style lighting like product photos
export const BALANCED_LIGHTING = {
  ambient: {
    intensity: 3.5, // Very bright ambient (was 2.8)
    color: '#ffffff'
  },
  spot: {
    position: [10, 10, 10],
    angle: 0.3,
    penumbra: 0.2,
    intensity: 5.0, // Much brighter (was 4.0)
    castShadow: true
  },
  rightFill: {
    position: [15, 8, 5],
    intensity: 4.5, // Very bright fill (was 3.5)
    color: '#ffffff',
    castShadow: false
  },
  leftFill: {
    position: [-15, 8, 5],
    intensity: 4.5, // Match right (was 3.5)
    color: '#ffffff',
    castShadow: false
  },
  directional: {
    position: [-8, 12, 8],
    intensity: 7.5, // Very bright key (was 6.0)
    color: '#ffffff',
    castShadow: true
  }
}

// Realistic lighting - more dramatic, better depth and realism
export const REALISTIC_LIGHTING = {
  ambient: {
    intensity: 0.5, // Much lower ambient for dramatic shadows
    color: '#ffffff'
  },
  spot: {
    position: [10, 10, 10],
    angle: 0.3,
    penumbra: 0.2,
    intensity: 4.0, // Moderate accent (reduced from 5.5)
    castShadow: true
  },
  rightFill: {
    position: [15, 8, 5],
    intensity: 2.0, // Much darker right side (was 3.5)
    color: '#ffffff',
    castShadow: false
  },
  leftFill: {
    position: [-15, 8, 5],
    intensity: 3.5, // Brighter left for contrast
    color: '#ffffff',
    castShadow: false
  },
  directional: {
    position: [-8, 12, 8],
    intensity: 7, // Strong but not extreme (was 9)
    color: '#ffffff',
    castShadow: true
  }
}

// Default export (will be switched based on mode)asdasdasd
export const LIGHTING_CONFIG = BALANCED_LIGHTING

export default {
  useCerakoteMaterials,
  CERAKOTE_PROPS,
  LIGHTING_CONFIG,
  BALANCED_LIGHTING,
  REALISTIC_LIGHTING
}
